/**
 * rb-desktop-scale-v1
 * Desktop (fine pointer) only: keep a laptop-width canvas; scale down as the window shrinks.
 * True phones/tablets are untouched — resized desktop windows never enter phone-stage.
 *
 * Uses transform:scale (not CSS zoom) so layout metrics and visuals stay in sync.
 */
(function () {
  var DESIGN = 1440;
  var designPx = DESIGN + 'px';
  var fineMq;
  var coarseMq;
  var heightTimer;

  try {
    fineMq = window.matchMedia('(hover: hover) and (pointer: fine)');
    coarseMq = window.matchMedia('(pointer: coarse)');
  } catch (e) {
    fineMq = { matches: false };
    coarseMq = { matches: true };
  }

  function isDesktopClient() {
    try {
      if (window.Shopify && window.Shopify.designMode) return false;
    } catch (e) {}
    if (!fineMq.matches) return false;
    if (coarseMq.matches) return false;
    /* iPadOS desktop-UA spoof still has multi-touch */
    if ((navigator.maxTouchPoints || 0) > 1 && /Macintosh/.test(navigator.userAgent || '')) {
      return false;
    }
    return true;
  }

  window.rbIsDesktopClient = isDesktopClient;

  function clearScale(root) {
    root.classList.remove('rb-desktop-scale');
    root.style.removeProperty('zoom');
    root.style.removeProperty('width');
    root.style.removeProperty('min-width');
    root.style.removeProperty('height');
    root.style.removeProperty('transform');
    root.style.removeProperty('transform-origin');
    root.style.removeProperty('overflow-x');
    root.style.removeProperty('--rb-desktop-scale');
    root.style.removeProperty('--rb-desktop-design');
  }

  function ageGateHoldsScale(root) {
    /* Crawlers never see the gate, so it must not hold the layout back either —
       otherwise they render an unscaled page no shopper is ever served. */
    if (window.rbIsCrawler) return false;
    if (root.classList.contains('rb-age-gate-open')) return true;
    try {
      if (window.localStorage.getItem('rb_age_verified') === '1') return false;
    } catch (e) {}
    /* Unverified: do not scale html before/while the gate is up.
       transform + html.height clip position:fixed overlays in every browser. */
    var gate = document.getElementById('rb-age-gate');
    if (!gate) return true;
    return gate.classList.contains('is-open') || gate.open === true;
  }

  function syncHeight(root, scale) {
    /* Native / overlay PDP owns scrolling (html overflow:hidden + .rb-pdp-card__scroll).
       Shrinking html.height by scale clips the fixed card to ~scale² of the viewport:
       blank page background on the lower half, and nothing to scroll. */
    if (root.classList.contains('rb-pdp-card-open') || ageGateHoldsScale(root)) {
      root.style.removeProperty('height');
      return;
    }
    root.style.height = 'auto';
    /* Layout height at design width, then shrink scrollport to the scaled visual height */
    var layoutH = Math.max(
      document.body ? document.body.scrollHeight : 0,
      root.scrollHeight,
      root.offsetHeight
    );
    root.style.height = Math.ceil(layoutH * scale) + 'px';
  }

  var lastAgeHold = false;

  function sync() {
    var root = document.documentElement;
    var desktop = isDesktopClient();
    root.classList.toggle('rb-desktop-client', desktop);
    lastAgeHold = ageGateHoldsScale(root);

    if (!desktop) {
      clearScale(root);
      notifyScale();
      return;
    }

    root.classList.remove('rb-phone-stage', 'rb-phone-stage-rail');
    root.style.setProperty('--rb-desktop-design', designPx);

    /* rb-age-gate-hold-scale-v1 — scaling html moves/clips the age gate off-screen
       and leaves a blurry unclickable page (Chrome, Brave, Safari, Firefox). */
    if (ageGateHoldsScale(root)) {
      clearScale(root);
      notifyScale();
      return;
    }

    var w = window.innerWidth;
    if (w >= DESIGN) {
      clearScale(root);
      notifyScale();
      return;
    }

    var scale = w / DESIGN;
    if (scale < 0.35) scale = 0.35;

    root.classList.add('rb-desktop-scale');
    root.style.setProperty('--rb-desktop-scale', String(scale));
    root.style.width = designPx;
    root.style.minWidth = designPx;
    root.style.transformOrigin = 'top left';
    root.style.transform = 'scale(' + scale + ')';
    root.style.overflowX = 'hidden';
    root.style.removeProperty('zoom');

    clearTimeout(heightTimer);
    heightTimer = setTimeout(function () {
      syncHeight(root, scale);
      notifyScale();
    }, 50);
    syncHeight(root, scale);
    notifyScale();
  }

  function notifyScale() {
    if (typeof window.rbSyncHeaderHeight === 'function') window.rbSyncHeaderHeight();
    try { window.dispatchEvent(new Event('rb-desktop-scale')); } catch (e) {}
  }

  window.rbSyncDesktopScale = sync;

  try {
    new MutationObserver(function () {
      if (ageGateHoldsScale(document.documentElement) !== lastAgeHold) sync();
    }).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
  } catch (e) {}

  sync();
  window.addEventListener('resize', sync, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', sync, { passive: true });
  }
  if (fineMq.addEventListener) fineMq.addEventListener('change', sync);
  else if (fineMq.addListener) fineMq.addListener(sync);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync, { once: true });
  }
  window.addEventListener('load', sync, { once: true });

  /* rb-bottom-lock-v1 — desktop pages end at the footer. The scaled html.height is a snapshot
     that can run past it while content settles, and trackpad overscroll kept nudging below it. */
  function lockBottom() {
    var root = document.documentElement;
    if (!root.classList.contains('rb-desktop-client') || root.classList.contains('rb-pdp-card-open')) return;
    var footer = document.querySelector('.rb-footer');
    if (!footer || !footer.offsetHeight) return;
    var end = Math.ceil(footer.getBoundingClientRect().bottom + window.scrollY);
    if (root.classList.contains('rb-desktop-scale') && Math.abs((parseFloat(root.style.height) || 0) - end) > 1) {
      root.style.setProperty('height', end + 'px', 'important');
    }
    var max = end - window.innerHeight;
    if (max > 0 && window.scrollY > max + 1) window.scrollTo(window.scrollX, max);
  }
  try {
    var bottomCss = document.createElement('style');
    bottomCss.textContent = 'html.rb-desktop-client,html.rb-desktop-client body{overscroll-behavior-y:none}';
    document.head.appendChild(bottomCss);
  } catch (e) {}
  window.addEventListener('scroll', lockBottom, { passive: true });
  window.addEventListener('rb-desktop-scale', function () { setTimeout(lockBottom, 60); });
  window.addEventListener('load', function () { setTimeout(lockBottom, 300); }, { once: true });
})();
